import type { AdminGate } from '../../utils/adminGate'
import { NOT_MEASURED, errorKind } from '../../utils/format'
import { isApiError, problemDetailText } from '../../services/apiError'
import type { BackstopActionKind } from '../../services/requestBackstopApi'
import type {
  BackstopReason, BackstopRequest, BackstopRequestDetail, RequestBackstopSummary,
} from '../../types/requestBackstop'

/**
 * The request backstop's words and decisions, apart from React so they can be tested in node.
 *
 * The backstop is rung 3 of doc 10's ladder: what FELL THROUGH, never everything open. Its emptiness is success, and
 * its controls are interventions — never the ordinary approve and refuse, which belong to the people who own the
 * content. Which interventions a request offers is the server's answer (the detail's `verbs` and `operations`, the
 * matrix evaluated for a Lighthouse administrator); this module keeps no copy of the matrix.
 */

/** The empty state (doc 10 §1): an empty backstop means the first two rungs are working. */
export const BACKSTOP_EMPTY_TITLE = 'Nothing has fallen through'

/** The empty state's second half, so the two read as doc 15 §7.1's one sentence. */
export const BACKSTOP_EMPTY_MESSAGE = 'Every open request is with the person whose move it is.'

/** What the page is, said on every visit. */
export const BACKSTOP_SCOPE =
  'Only what fell through: a Cartographer request comes here when its decision is overdue, when nobody whose move it '
  + 'is could be told, or — platform access — as soon as it is raised, because only a Lighthouse administrator can move '
  + 'it. Approving or refusing access to content is not here: that belongs to the people who own the content.'

/** Why the page's controls can never approve anything. */
export const BACKSTOP_NO_APPROVE =
  'The backstop intervenes; it does not decide content. Force close, reassign, reroute and a corrected due date are '
  + 'offered where the request allows them, and platform access can be forwarded, attested or refused.'

/** The tier the backstop is, as the API names it. */
export const BACKSTOP_TIER = 'Admin'

/**
 * What a stale page is told when it acts on a request that has left the backstop (`NotOnBackstop`, a 409). The server
 * sends this sentence; this copy is the fallback for a refusal that carries no detail.
 */
export const NOT_ON_BACKSTOP = 'This request is no longer on the backstop. Reload to see where it is now.'

/**
 * Whether the drawer's intervention panel renders: when there is something to offer or to explain — or a refusal to
 * show. A NotOnBackstop refusal re-reads the request, which then offers nothing; its words must outlive that reload.
 */
export function showInterventionPanel(
  controls: { actions: unknown[]; deniedReason: string | null },
  failed: boolean,
): boolean {
  return controls.actions.length > 0 || controls.deniedReason != null || failed
}

const REASONS: Record<BackstopReason, { label: string; hint: string }> = {
  Overdue: {
    label: 'Overdue',
    hint: 'Its decision was due and nobody made it: the hourly sweep handed it here on its backstop date.',
  },
  NobodyCouldBeTold: {
    label: 'Nobody could be told',
    hint: 'Nobody whose move it is could receive a notice — they have never signed in to Cartographer, or the request '
      + 'has nobody left to decide it — so it came here at once.',
  },
  AwaitingLighthouseAdmin: {
    label: 'Awaiting a Lighthouse administrator',
    hint: 'Platform access: nobody inside Cartographer can decide it, so it starts here.',
  },
}

/** A reason's label, or the raw name when this build does not know it. */
export function reasonLabel(reason: string | null | undefined): string {
  if (!reason) return NOT_MEASURED
  return REASONS[reason as BackstopReason]?.label ?? reason
}

export function reasonHint(reason: string | null | undefined): string | null {
  if (!reason) return null
  return REASONS[reason as BackstopReason]?.hint ?? null
}

const KINDS: Record<string, string> = {
  Commission: 'Commission',
  ContentAccess: 'Access to content',
  PlatformAccess: 'Platform access',
  Endorsement: 'Endorsement',
}

export function kindLabel(kind: string): string {
  return KINDS[kind] ?? kind
}

const STATES: Record<string, string> = {
  Open: 'Open',
  Assigned: 'Assigned',
  Forwarded: 'Forwarded to Entra',
  Delivered: 'Delivered',
  Approved: 'Approved',
  Refused: 'Refused',
  Granted: 'Granted (attested)',
  Completed: 'Completed',
  Closed: 'Closed unbuilt',
  ForceClosed: 'Force closed',
  Withdrawn: 'Withdrawn',
}

export function stateLabel(state: string): string {
  return STATES[state] ?? state
}

const ROUTES: Record<string, string> = {
  TargetOwner: "The report's owner",
  WorkspaceOwner: "The workspace's owner",
  CartographerAdmins: 'The Cartographer administrators',
  LighthouseAdmins: 'The Lighthouse administrators',
}

export function routeLabel(route: string): string {
  return ROUTES[route] ?? route
}

/** Who it was routed to: the stamped name when there is one, else the class of party. */
export function routedToText(request: Pick<BackstopRequest, 'routedTo' | 'routedToDisplayName'>): string {
  const name = request.routedToDisplayName?.trim()
  return name ? `${name} (${routeLabel(request.routedTo).toLowerCase()})` : routeLabel(request.routedTo)
}

/** The target as served, or a plain statement that it is gone — never a blank. */
function targetText(request: BackstopRequest): string {
  const name = request.targetName?.trim()
  if (name) return name
  return request.targetKind === 'Workspace' ? '(a workspace no longer available)' : '(a report no longer available)'
}

/** What the request asks for, in one line. */
export function subjectLine(request: BackstopRequest): string {
  switch (request.kind) {
    case 'PlatformAccess': {
      const who = request.subjectText?.trim() || NOT_MEASURED
      return request.licenceLevel?.trim() ? `Access to Cartographer for ${who} (${request.licenceLevel.trim()})` : `Access to Cartographer for ${who}`
    }
    case 'Commission':
      return `“${request.title?.trim() || 'Untitled'}” in ${targetText(request)}`
    case 'ContentAccess': {
      const role = request.requestedRole ?? 'Access'
      const term = request.accessDurationDays == null ? 'permanently' : `for ${request.accessDurationDays} days`
      return `${role} on ${targetText(request)}, ${term}`
    }
    case 'Endorsement':
      return `Endorse ${targetText(request)}`
    default:
      return targetText(request)
  }
}

/** The header's count line. Every count is the server's; nothing is computed by subtraction. */
export function summaryLine(summary: RequestBackstopSummary): string {
  if (!summary.storeProvisioned || summary.openCount == null) return NOT_MEASURED
  if (summary.openCount === 0) return 'Nothing on the backstop'
  const parts = [
    `${summary.openCount} on the backstop`,
    `${summary.overdueCount ?? 0} overdue`,
    `${summary.nobodyCouldBeToldCount ?? 0} nobody could be told about`,
    `${summary.awaitingLighthouseAdminCount ?? 0} platform access awaiting a Lighthouse administrator`,
  ]
  return parts.join(' · ')
}

/** The rail badge: the open count, only when it was measured and is above zero. */
export function requestBackstopBadge(summary: RequestBackstopSummary | undefined): number | undefined {
  if (!summary?.storeProvisioned) return undefined
  const count = summary.openCount
  return count != null && count > 0 ? count : undefined
}

// ---- interventions -------------------------------------------------------------------------------------------

/** What an intervention asks the administrator for. */
export type InterventionInput = 'reason' | 'note' | 'assignee' | 'route' | 'date'

export interface InterventionSpec {
  action: BackstopActionKind
  label: string
  input: InterventionInput
  /** Whether the input is required before it can be sent. */
  required: boolean
  /** What the form says before it is sent. */
  explain: string
  /** Red: it ends the request. */
  ends: boolean
}

/** Every intervention, in the order the drawer offers them; each keyed by the matrix name that enables it. */
const INTERVENTIONS: { matrix: string; source: 'verb' | 'operation'; spec: InterventionSpec }[] = [
  {
    matrix: 'Forward', source: 'verb',
    spec: {
      action: 'forward', label: 'Forward to Entra', input: 'note', required: false, ends: false,
      explain: 'Pass it to the Entra administrator. The request waits on Entra with a new due date; the note goes into '
        + 'the audit row.',
    },
  },
  {
    matrix: 'Grant', source: 'verb',
    spec: {
      action: 'attest', label: 'Attest granted', input: 'note', required: false, ends: true,
      explain: 'Say that Entra granted it. Never on a request you raised yourself: another administrator must attest it.',
    },
  },
  {
    matrix: 'Reassign', source: 'operation',
    spec: {
      action: 'reassign', label: 'Reassign', input: 'assignee', required: true, ends: false,
      explain: 'Hand the commission to another live member of its workspace. They are told.',
    },
  },
  {
    matrix: 'Reroute', source: 'operation',
    spec: {
      action: 'reroute', label: 'Reroute', input: 'route', required: true, ends: false,
      explain: 'Send it to someone who can act on it. Rerouting changes no clock, so it stays on the backstop until its '
        + 'due date is corrected — give them a date as well to take it off.',
    },
  },
  {
    matrix: 'SetDueAt', source: 'operation',
    spec: {
      action: 'due-date', label: 'Correct the due date', input: 'date', required: true, ends: false,
      explain: 'The only intervention that takes a request off the backstop: its ladder starts again from the new date.',
    },
  },
  {
    matrix: 'Refuse', source: 'verb',
    spec: {
      action: 'refuse', label: 'Refuse', input: 'reason', required: true, ends: true,
      explain: 'Platform access only: say no, with a reason the requester will read.',
    },
  },
  {
    matrix: 'ForceClose', source: 'verb',
    spec: {
      action: 'force-close', label: 'Force close', input: 'reason', required: true, ends: true,
      explain: 'Close it as it stands, with a reason the requester will read. Nothing it asked for is granted.',
    },
  },
]

/**
 * The interventions the drawer offers: what the server says a Lighthouse administrator may do from this state, and
 * ONLY when the write gate is `admin` (the catalogue's `canHandleRequests`). A `denied` gate says why; `pending` and
 * `unknown` show nothing. A request that is not on the backstop — ended, or corrected back to its owner — offers
 * nothing: the server refuses every verb on it (`NotOnBackstop`, ruled 2026-10-08) and serves none.
 */
export function interventionControls(
  gate: AdminGate,
  detail: Pick<BackstopRequestDetail, 'request' | 'verbs' | 'operations'>,
): { actions: InterventionSpec[]; deniedReason: string | null } {
  if (detail.request.resolvedAt != null || !detail.request.onBackstop) return { actions: [], deniedReason: null }
  if (gate.state === 'denied') return { actions: [], deniedReason: gate.reason || null }
  if (gate.state !== 'admin') return { actions: [], deniedReason: null }

  const actions = INTERVENTIONS
    .filter(({ matrix, source }) => (source === 'verb' ? detail.verbs : detail.operations).includes(matrix))
    .map(({ spec }) => spec)
  return { actions, deniedReason: null }
}

/** A refusal, worded by the store's code, then the status. */
export function interventionErrorText(error: unknown): string {
  const kind = errorKind(error)
  if (kind === 'sign-in-required') {
    return 'Not sent: this action accepts only an Entra sign-in; the shared API key is refused even locally. Sign in again.'
  }
  if (kind === 'unauthorized') {
    return 'Refused: this action accepts only an Entra sign-in; the shared API key is refused even locally. Sign in again.'
  }

  const detail = isApiError(error) ? problemDetailText(error.body) : null
  const code = isApiError(error) && typeof error.body === 'object' && error.body !== null
    ? (error.body as Record<string, unknown>).code
    : undefined

  switch (code) {
    case 'NotOnBackstop':
      // The store's own words. The 409 has already re-read the request, so the drawer shows where it went.
      return detail ?? NOT_ON_BACKSTOP
    case 'Stale':
      return detail ?? 'Somebody changed this request since you read it. It has been re-read; nothing was changed.'
    case 'IllegalTransition':
      return detail ?? 'This request has moved on, so that is no longer possible. Nothing was changed.'
    case 'SelfDecisionRefused':
      return detail ?? 'You raised this request, so another administrator must attest it. Nothing was changed.'
  }

  switch (kind) {
    case 'forbidden':
      return detail ?? 'Intervening on a request requires the Lighthouse.Admin app role, which this account does not hold.'
    case 'not-found':
      return detail ?? 'No such request. Nothing was changed.'
    case 'conflict':
      return detail ?? 'The request changed since you read it. Nothing was changed.'
    case 'bad-request':
      return detail ?? 'The request was refused as sent. Nothing was changed.'
    case 'network':
      return 'Could not reach the API. Nothing is known to have changed.'
    default:
      return detail ?? 'The API failed. Nothing is known to have changed; read the request again before retrying.'
  }
}

/** The toast after an intervention, from the request as it now stands. */
export function interventionToast(spec: Pick<InterventionSpec, 'label'>, result: BackstopRequest): { title: string; message: string } {
  const where = result.onBackstop ? 'It is still on the backstop.' : 'It is off the backstop.'
  return { title: `${spec.label}: done`, message: `${stateLabel(result.state)}. ${where}` }
}

/**
 * A date input's `YYYY-MM-DD` as the instant the API takes: the END of that day where the administrator is, so "until
 * Friday" means all of Friday. Null for anything that is not a real calendar date, or whose end is not after `now` —
 * the store refuses a due date that is not in the future, and the form should say so before sending.
 */
export function dueAtFromDateInput(value: string, now: number = Date.now()): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim())
  if (!match) return null
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])]
  const end = new Date(year, month - 1, day, 23, 59, 59)
  if (end.getFullYear() !== year || end.getMonth() !== month - 1 || end.getDate() !== day) return null
  return end.getTime() > now ? end.toISOString() : null
}

const OPERATIONS: Record<string, string> = {
  RequestCreated: 'Raised',
  RequestAssigned: 'Assigned',
  RequestReassigned: 'Reassigned',
  RequestRerouted: 'Rerouted',
  RequestDueDateChanged: 'Due date corrected',
  RequestForwarded: 'Forwarded to Entra',
  RequestDelivered: 'Delivered',
  RequestSentBack: 'Sent back',
  RequestRetractionRequested: 'Retraction asked for',
  RequestApproved: 'Approved',
  RequestRefused: 'Refused',
  RequestGranted: 'Attested granted',
  RequestCompleted: 'Completed',
  RequestClosed: 'Closed unbuilt',
  RequestForceClosed: 'Force closed',
  RequestWithdrawn: 'Withdrawn',
}

/** One audit row's operation, in words. */
export function operationLabel(operation: string): string {
  return OPERATIONS[operation] ?? operation
}

/** The audit trail's view of one request: the `requests` category, narrowed to its id. */
export function requestAuditLink(id: string): string {
  return `/audit?${new URLSearchParams({ category: 'requests', entityId: id }).toString()}`
}
